import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import PaymentGateway from '../../payment/PaymentGateway';
import doctorService from '../../../services/doctorService';
import { 
  CreditCard, 
  ArrowLeft, 
  CheckCircle, 
  User, 
  Calendar, 
  Clock, 
  Phone,
  UserCheck,
  Loader,
  MapPin,
  Users
} from 'lucide-react';

const Checkout = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const { doctor, slot, formData, consultationType } = state || {};
  const [isLoading, setIsLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [appointmentId, setAppointmentId] = useState(null); // Store appointment ID after booking
  
  // Calculate appointment fee based on consultation type
  const appointmentFee = consultationType === 'priority' 
    ? (doctor?.price_hospital_priority || 500)
    : (doctor?.price_hospital_normal || 400);

  const handlePaymentSuccess = async (paymentData) => {
    // Payment is complete, appointment already booked and will be updated via webhook
    // Just navigate to success page
    navigate(`/order-success?order_id=${appointmentId}&total=${appointmentFee}&payment_id=${paymentData.razorpay_payment_id || 'PAID'}&service_type=doctor_appointment`);
  };
  
  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    setPaymentError('Payment failed. Please try again.');
    setIsLoading(false);
  };
  
  const initiatePayment = async () => {
    if (!token) {
      alert('You must be logged in to complete payment.');
      navigate('/login');
      return;
    }
    
    setIsLoading(true);
    setPaymentError('');

    try {
      // Book appointment first
      let response;
      const message = `Patient: ${formData.name}, Age: ${formData.age}, Gender: ${formData.gender}, Phone: ${formData.phone}`;
      const patientGender = formData.gender ? formData.gender.toLowerCase() : undefined;
      
      if (consultationType === 'priority') {
        // Book priority appointment with time slot
        // Convert "11:00 AM" → "11:00:00" (24-hour ISO format)
        let timeStr = '09:00:00';
        if (slot.time) {
          const parsed = new Date(`1970-01-01 ${slot.time}`);
          if (!isNaN(parsed)) {
            timeStr = parsed.toTimeString().slice(0, 8); // "HH:MM:SS"
          } else {
            // already 24-hour format like "11:00" or "11:00:00"
            timeStr = slot.time.length === 5 ? `${slot.time}:00` : slot.time;
          }
        }
        const scheduledTime = `${slot.date}T${timeStr}`;

        // Validate slot time
        const validation = doctorService.validateSlotTime(scheduledTime);
        if (!validation.valid) {
          setPaymentError(validation.message);
          setIsLoading(false);
          return;
        }

        response = await doctorService.bookPriorityAppointment({
          doctor_id: doctor.id,
          scheduled_time: scheduledTime,
          message: message,
          patient_address: formData.patient_address || undefined,
          patient_gender: patientGender,
          care_of: formData.care_of || undefined,
        });
      } else {
        // Book normal appointment (queue-based, date only)
        response = await doctorService.bookNormalAppointment({
          doctor_id: doctor.id,
          visit_date: slot.date,
          message: message,
          patient_address: formData.patient_address || undefined,
          patient_gender: patientGender,
          care_of: formData.care_of || undefined,
        });
      }

      // Store appointment ID for payment
      // API returns { success, message, data: { id, ... } }
      const aptId = response.data?.id || response.id;
      setAppointmentId(aptId);
      
      // Show payment gateway
      setIsLoading(false);
      setShowPayment(true);
    } catch (error) {
      console.error('Appointment booking failed:', error);
      setPaymentError(error.message || 'Failed to book appointment. Please try again.');
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    if (isNaN(date)) return dateStr;
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    let time = new Date(`2000-01-01 ${timeStr}`);
    if (isNaN(time)) time = new Date(`2000-01-01T${timeStr}`);
    if (isNaN(time)) return timeStr;
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  if (!doctor || !slot || !formData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">Missing booking information.</p>
          <button 
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-800"
          >
            Start over
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
        <Header />
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button 
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
            disabled={isLoading}
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Patient Details
          </button>
        </div>

        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-8 text-white">
            <div className="flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mx-auto mb-4">
              <CreditCard className="h-8 w-8" />
            </div>
            <h1 className="text-3xl font-bold text-center mb-4">Checkout</h1>
            <p className="text-green-100 text-center">
              Review your appointment details and confirm booking
            </p>
          </div>

          {/* Booking Summary */}
          <div className="px-8 py-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Appointment Summary
            </h2>

            <div className="space-y-6">
              {/* Doctor Information */}
              <div className="bg-blue-50 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserCheck className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Healthcare Provider
                    </h3>
                    <p className="text-2xl font-bold text-blue-600 mb-1">
                      Dr. {doctor.name}
                    </p>
                    <p className="text-gray-600">{doctor.specialty}</p>
                  </div>
                </div>
              </div>

              {/* Appointment Time */}
              <div className={`rounded-2xl p-6 ${
                consultationType === 'priority' ? 'bg-purple-50' : 'bg-green-50'
              }`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    consultationType === 'priority' ? 'bg-purple-100' : 'bg-green-100'
                  }`}>
                    <Calendar className={`h-6 w-6 ${
                      consultationType === 'priority' ? 'text-purple-600' : 'text-green-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Appointment Schedule
                      </h3>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        consultationType === 'priority' 
                          ? 'bg-purple-200 text-purple-800' 
                          : 'bg-green-200 text-green-800'
                      }`}>
                        {consultationType === 'priority' ? 'Priority' : 'Normal'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Date</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatDate(slot.date)}
                        </p>
                      </div>
                      {consultationType === 'priority' && slot.time ? (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Time</p>
                          <p className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Clock className={`h-4 w-4 ${
                              consultationType === 'priority' ? 'text-purple-600' : 'text-green-600'
                            }`} />
                            {formatTime(slot.time)}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Time</p>
                          <p className="text-sm text-gray-500 italic">
                            Based on arrival at hospital
                          </p>
                        </div>
                      )}
                    </div>
                    {consultationType === 'normal' && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-800">
                          <strong>Note:</strong> This is a normal consultation. The doctor will see you based on your arrival time at the hospital (first come, first served).
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Patient Information */}
              <div className="bg-purple-50 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Patient Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Name</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formData.name}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Age & Gender</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formData.age} years, {formData.gender}
                        </p>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-sm text-gray-600 mb-1">Contact</p>
                        <p className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <Phone className="h-4 w-4 text-purple-600" />
                          {formData.phone}
                        </p>
                      </div>
                      {formData.patient_address && (
                        <div className="sm:col-span-2">
                          <p className="text-sm text-gray-600 mb-1">Address</p>
                          <p className="text-base font-medium text-gray-900 flex items-start gap-2">
                            <MapPin className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            {formData.patient_address}
                          </p>
                        </div>
                      )}
                      {formData.care_of && (
                        <div className="sm:col-span-2">
                          <p className="text-sm text-gray-600 mb-1">Accompanying Person</p>
                          <p className="text-base font-medium text-gray-900 flex items-center gap-2">
                            <Users className="h-4 w-4 text-purple-600" />
                            {formData.care_of}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="px-8 py-8 bg-gray-50 border-t">
            {/* Appointment Fee */}
            <div className="bg-white rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Consultation Fee:</span>
                <span className="text-2xl font-bold" style={{ color: '#104a5f' }}>₹{appointmentFee}</span>
              </div>
            </div>

            {/* Error Message */}
            {paymentError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {paymentError}
              </div>
            )}

            {!showPayment ? (
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Ready to confirm your appointment</span>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Complete payment to confirm your appointment
                </p>
                <button
                  onClick={initiatePayment}
                  className="w-full text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:opacity-90"
                  style={{ backgroundColor: '#104a5f' }}
                >
                  <CreditCard className="h-5 w-5" />
                  Pay ₹{appointmentFee} & Confirm
                </button>
              </div>
            ) : (
              <PaymentGateway
                amount={appointmentFee}
                serviceType="doctor_appointment"
                serviceId={appointmentId}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                customerDetails={{
                  name: user?.name || formData.name || '',
                  email: user?.email || '',
                  mobile: user?.mobile || formData.phone || ''
                }}
              />
            )}

            {isLoading && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 text-center">
                  <Loader className="h-12 w-12 animate-spin mx-auto mb-4" style={{ color: '#104a5f' }} />
                  <p className="text-gray-600">Processing your appointment...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    <Footer />
      </>
  );
};

export default Checkout;